import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from "@angular/core";
import { VTableComponent } from "@app/directives/_layout/v-table/_components/v-table.component";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { VerticalFilterItemCreationCallback } from "@app/directives/_resourceFilters/vertical-filter-list/vertical-filter-list.component";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { DateService, DateUnit } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { map } from "rxjs/operators";
import { CreationCode } from "../../_enums/CreationCode.enum";
import { ICameraDiagnosticRecord } from "../../_models/ICameraDiagnosticRecord";
import { CameraEventService } from "../../_services/camera-events.service";
import './camera-events.component.scss';

@UntilDestroy()
@Component({
    selector: "camera-events",
    template: require('./camera-events.component.html')
})
export class CameraEventsComponent implements OnInit {
    @Input() serial: string;
    @ViewChild('diagMessagesTable') diagMessagesTable: VTableComponent;

    events: ICameraDiagnosticRecord[];
    selectedEvent: ICameraDiagnosticRecord;

    startDate: Date;
    endDate: Date;
    defaultDateRange: DateRange = DateRange.Today;

    tableColumns: IVTableColumn<ICameraDiagnosticRecord>[];
    tableRows: IVTableRow[] = [];

    loading: boolean;
    liveUpdates: boolean = true;

    CreationCode = CreationCode;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly cameraEventService: CameraEventService,
        private readonly dateService: DateService,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
        this.setDefaultDate();
        this.createTable();
    }

    selectEvent(row: IVTableRow): void {
        this.selectedEvent = row.data;
    }

    deselectEvent(): void {
        this.selectedEvent = null;
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<ICameraDiagnosticRecord>): void {
        if (!filterEvent.trigger) {
            return;
        }

        if (filterEvent.trigger == "date") {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.setLiveUpdates();
            this.getData();
        }
    }

    creationCodeItemCreationCallback: VerticalFilterItemCreationCallback<ICameraDiagnosticRecord> = (event) => {

        return {
            name: this.translationService.tEnum(CreationCode, "CreationCode", event.parsedPayload['creationCode']),
            icon: '',
            data: event,
            color: 'black',
            predicate: (d) => d.parsedPayload['creationCode'] === event.parsedPayload['creationCode']
        };
    }

    additionalDataItemCreationCallback: VerticalFilterItemCreationCallback<ICameraDiagnosticRecord> = (event) => {

        return {
            name: event.parsedPayload['additionalDataName'] || this.translationService.t("Common.None"),
            icon: '',
            data: event,
            color: 'black',
            predicate: (d) => d.parsedPayload['additionalDataName'] === event.parsedPayload['additionalDataName']
        };
    }

    //--

    private getData(): void {
        this.loading = true;

        this.cameraEventService.getCameraEvents({ Serial: this.serial, Start: this.startDate, End: this.endDate }).pipe(
            untilDestroyed(this),
            map(records => {
                this.events = records;
                this.createTableRows();
                this.loading = false;
            })
        ).subscribe();
    }

    private setDefaultDate(): void {
        const dateRange = this.dateService.dateRangeFromEnum(this.defaultDateRange);
        this.startDate = dateRange.start;
        this.endDate = dateRange.end;
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "DeviceEvents.ReceivedDate", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: event => this.vTableService.createDateCell(event.CreatedTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "DeviceEvents.EventDate" }),
                createCellFunction: event => this.vTableService.createDateCell(event.EventTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "DeviceEvents.Delay" }),
                createCellFunction: event => this.vTableService.createDurationCell(new Date(event.CreatedTime).getTime() - new Date(event.EventTime).getTime())
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceEvents.CreationCode", width: 150 }),
                createCellFunction: event => this.vTableService.createEnumCell(CreationCode, "CreationCode", event.parsedPayload["creationCode"])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceEvents.SeqNumber", width: 150 }),
                createCellFunction: event => this.vTableService.createTableCell(event.parsedPayload["SeqNumber"])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceEvents.IgnitionStatus", width: 150 }),
                createCellFunction: event => this.vTableService.createTableCell(event.parsedPayload["ignitionStatus"] ? this.translationService.t("Common.On") : this.translationService.t("Common.Off"))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceEvents.AdditionalDataName", width: 200 }),
                createCellFunction: event => this.vTableService.createTableCell(event.parsedPayload["additionalDataName"])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceEvents.AdditionalDataValue" }),
                createCellFunction: event => this.vTableService.createTableCell(event.parsedPayload["additionalDataValue"])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceEvents.GsmStrength", width: 150 }),
                createCellFunction: event => this.vTableService.createTableCell(event.parsedPayload["gsmSignalStrength"])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceEvents.SatCount", width: 150 }),
                createCellFunction: event => this.vTableService.createTableCell(event.parsedPayload["GPS"]["satelitecount"])
            }
        ]
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.events, this.tableColumns);
    }

    private setLiveUpdates(): void {
        // Only subscribe to new messages if the end date is within the last 15 minutes or in the future
        this.liveUpdates = this.dateService.date1IsAfterDate2(this.endDate, this.dateService.addOrSubtractTime(new Date(), -15, DateUnit.Min));
    }
}
